import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate, useBlocker } from 'react-router-dom';
import './DashboardView.css';
import './DeviceDetail.css';
import { API_BASE, authHeaders } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { useFocusTrap } from '../hooks/useFocusTrap';
import DashboardEditor from './dashboard/DashboardEditor';
import { Zurück, Bearbeiten, Löschen, Abbrechen } from '../icons';

/** ThingsBoard UI base URL (ohne /api) */
const TB_BASE = API_BASE.replace(/\/api\/?$/, '');

function DashboardView() {
  const { dashboardId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { authToken: token } = useAuth();

  const [dashboard, setDashboard] = useState(() => {
    const nameFromState = location?.state?.name;
    return nameFromState ? { name: nameFromState } : null;
  });
  const titleRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deleteConfirmRef = useRef(null);

  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [editMode, setEditMode] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [editorDirty, setEditorDirty] = useState(false);

  // Navigation blockieren wenn Editor ungespeicherte Änderungen hat
  const blocker = useBlocker(editMode && editorDirty);

  useFocusTrap(showDeleteConfirm, deleteConfirmRef);

  // Warn on browser tab close / refresh when editor has unsaved changes
  useEffect(() => {
    function onBeforeUnload(e) {
      if (editMode && editorDirty) { e.preventDefault(); e.returnValue = ''; return ''; }
      return undefined;
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [editMode, editorDirty]);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/dashboard/${dashboardId}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Fehler beim Laden des Dashboards (HTTP ${res.status})`);
      }
      const data = await res.json();
      setDashboard({
        id: data.id?.id || dashboardId,
        name: data.name || data.title,
        description: data.description || '',
        createdTime: data.createdTime,
        configuration: data.configuration || null,
      });
    } catch (err) {
      setError(err.message || 'Dashboard konnte nicht geladen werden. Bitte prüfen Sie Ihre Netzwerkverbindung.');
    } finally {
      setLoading(false);
    }
  }, [token, dashboardId]);

  useEffect(() => {
    if (token && dashboardId) loadDashboard();
  }, [token, dashboardId, loadDashboard]);

  useEffect(() => {
    if (!loading && dashboard && titleRef.current) {
      try { titleRef.current.focus(); } catch {}
    }
  }, [loading, dashboard]);

  function showStatus(message, type = 'success') {
    setStatusType(type);
    setStatusMessage(message);
    setTimeout(() => setStatusMessage(''), 4000);
  }

  async function deleteDashboard() {
    try {
      setDeleting(true);
      const res = await fetch(`${API_BASE}/dashboard/${dashboardId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Dashboard konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      navigate('/dashboards');
    } catch (e) {
      console.error(e);
        showStatus(`Dashboard konnte nicht gelöscht werden: ${e.message || 'Netzwerkfehler'}.`, 'error');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  if (loading) return <p className="dashboard-view-loading">Lade Dashboard…</p>;
  if (error) return <div role="alert" className="dashboard-view-error">{error}</div>;
  if (!dashboard) return null;

  return (
    <div className="dashboard-view-page">

      {/* Header */}
      <div className="device-detail-header">
        <div className="header-left">
          <button
            className="back-button"
            onClick={() => navigate(-1)}
            aria-label="Zurück zur vorherigen Seite"
            title="Zurück zur vorherigen Seite"
          >
            <span aria-hidden="true"><Zurück /></span>
          </button>
          <h1 id="dashboard-view-title" tabIndex={-1} ref={titleRef} className="device-title">
            {dashboard.name}
            {editMode && <span className="edit-mode-badge">Bearbeitungsmodus</span>}
          </h1>
        </div>

        <div className="device-actions">
          {!editMode && (
            <button
              className="action-buttons"
              aria-label="Dashboard bearbeiten"
              title="Dashboard bearbeiten"
              onClick={() => setEditMode(true)}
            >
              <div className="action-button-icon"><Bearbeiten /></div>
              <div>Bearbeiten</div>
            </button>
          )}

          <button
            className="action-buttons"
            aria-label="Dashboard löschen"
            title="Dashboard löschen"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <div className="action-button-icon"><Löschen /></div>
            <div>Dashboard löschen</div>
          </button>
        </div>
      </div>

      {statusMessage && (
        <div className={`dashboard-view-status${statusType === 'error' ? ' dashboard-view-status--error' : ''}`} role="status" aria-live="polite">
          <p>{statusMessage}</p>
        </div>
      )}

      {/* Dashboard – Editor oder iframe-Ansicht */}
      <div className="dashboard-view-content">
        {editMode && dashboard.configuration ? (
          <DashboardEditor
            configuration={dashboard.configuration}
            dashboardId={dashboardId}
            token={token}
            onDirtyChange={setEditorDirty}
            onClose={(saved) => {
              setEditMode(false);
              setEditorDirty(false);
              if (saved) {
                // Nach Speichern: Dashboard + iframe neu laden
                loadDashboard();
                setIframeKey(k => k + 1);
              }
            }}
          />
        ) : token && dashboardId ? (
          <iframe
            key={iframeKey}
            title={dashboard.name || 'Dashboard'}
            src={`${TB_BASE}/dashboard/${dashboardId}?accessToken=${token}&hideToolbar=true`}
            className="dashboard-iframe"
            allowFullScreen
          />
        ) : (
          <p className="dashboard-view-no-config">Keine Dashboard-Konfiguration vorhanden.</p>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
        >
          <div className="modal" ref={deleteConfirmRef}>
            <h2 id="delete-confirm-title">
              Sind Sie sicher, dass Sie das Dashboard <br />
              <i>{dashboard.name}</i> löschen möchten?
            </h2>
            <p>
              Nach der Bestätigung wird das Dashboard und alle zugehörigen Daten{' '}
              <strong>unwiderruflich</strong> gelöscht.
            </p>
            <div className="modal-actions">
              <button
                className="modal-primary"
                onClick={deleteDashboard}
                disabled={deleting}
              >
                {deleting ? (
                  "Löschen…"
                ) : (
                  <>
                    <Löschen />
                    Löschen bestätigen
                  </>
                )}
              </button>
              <button
                className="modal-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                <span aria-hidden="true"><Abbrechen /></span> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation-Blocker Modal bei ungespeicherten Änderungen */}
      {blocker.state === 'blocked' && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="nav-confirm-title">
          <div className="modal">
            <h2 id="nav-confirm-title">
              Ungespeicherte Änderungen
            </h2>
            <p>
              Es gibt ungespeicherte Änderungen im Editor. Möchten Sie die Seite wirklich verlassen?
            </p>
            <div className="modal-actions">
              <button className="modal-primary" onClick={() => { setEditorDirty(false); setEditMode(false); blocker.proceed(); }}>
                Seite verlassen
              </button>
              <button className="modal-secondary" onClick={() => blocker.reset()}>
                ✕ Zurück
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardView;
